import { useQueryClient } from '@tanstack/react-query';
import { Edit3, Hash, Loader2, Merge, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTags } from '../hooks/useTags';
import type { Tag, UpdateTagRequest } from '../types/tags';

/**
 * Admin interface for tag management
 *
 * Features:
 * - View all tags with usage counts
 * - Search and filter tags
 * - Create new tags
 * - Edit tag names and descriptions
 * - Merge duplicate tags
 * - Delete unused tags
 * - Recompute usage counts
 */
export function TagsAdmin() {
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
	const [editingTag, setEditingTag] = useState<Tag | null>(null);
	const [editForm, setEditForm] = useState({ name: '', description: '' });
	const [showMergeModal, setShowMergeModal] = useState(false);
	const [mergeFromTag, setMergeFromTag] = useState<Tag | null>(null);
	const [mergeToTag, setMergeToTag] = useState<Tag | null>(null);

	const queryClient = useQueryClient();
	const { useTagsList, useUpdateTag, useMergeTags, useDeleteTag, useRecomputeUsage } = useTags();

	// Get paginated tags list
	const { data: tagsData, isLoading, error } = useTagsList(1, 100);
	const tags = tagsData?.data || [];

	// Mutations
	const updateTagMutation = useUpdateTag();
	const mergeTagsMutation = useMergeTags();
	const deleteTagMutation = useDeleteTag();
	const recomputeUsageMutation = useRecomputeUsage();

	// Filter tags based on search
	const filteredTags = tags.filter(
		tag =>
			tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			(tag.description && tag.description.toLowerCase().includes(searchQuery.toLowerCase()))
	);

	// Get HSL color from hue
	const getTagColor = (hue: number) => ({
		backgroundColor: `hsl(${hue}, 45%, 90%)`,
		color: `hsl(${hue}, 70%, 35%)`,
		borderColor: `hsl(${hue}, 45%, 80%)`,
	});

	// Handle tag selection
	const toggleTagSelection = (tagId: string) => {
		const newSelected = new Set(selectedTags);
		if (newSelected.has(tagId)) {
			newSelected.delete(tagId);
		} else {
			newSelected.add(tagId);
		}
		setSelectedTags(newSelected);
	};

	// Start editing a tag
	const startEditing = (tag: Tag) => {
		setEditingTag(tag);
		setEditForm({
			name: tag.name,
			description: tag.description || '',
		});
	};

	// Save tag edits
	const saveEdit = async () => {
		if (!editingTag) return;

		const updateData: UpdateTagRequest = {};
		if (editForm.name !== editingTag.name) {
			updateData.name = editForm.name.trim();
		}
		if (editForm.description !== (editingTag.description || '')) {
			updateData.description = editForm.description.trim() || undefined;
		}

		if (Object.keys(updateData).length > 0) {
			await updateTagMutation.mutateAsync({
				tagId: editingTag.id,
				data: updateData,
			});
		}

		setEditingTag(null);
		setEditForm({ name: '', description: '' });
	};

	// Cancel editing
	const cancelEdit = () => {
		setEditingTag(null);
		setEditForm({ name: '', description: '' });
	};

	// Start merge process
	const startMerge = (fromTag: Tag) => {
		setMergeFromTag(fromTag);
		setShowMergeModal(true);
	};

	// Execute merge
	const executeMerge = async () => {
		if (!mergeFromTag || !mergeToTag) return;

		await mergeTagsMutation.mutateAsync({
			fromId: mergeFromTag.id,
			toId: mergeToTag.id,
		});

		setShowMergeModal(false);
		setMergeFromTag(null);
		setMergeToTag(null);
	};

	// Delete selected tags
	const deleteSelectedTags = async () => {
		if (selectedTags.size === 0) return;

		const confirmMessage = `Are you sure you want to delete ${selectedTags.size} tag(s)? This action cannot be undone.`;
		if (!window.confirm(confirmMessage)) return;

		for (const tagId of selectedTags) {
			await deleteTagMutation.mutateAsync(tagId);
		}

		setSelectedTags(new Set());
	};

	// Recompute usage counts
	const recomputeUsage = async () => {
		if (!window.confirm('Recompute usage counts for all tags? This may take a moment.')) return;
		await recomputeUsageMutation.mutateAsync();
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center p-8">
				<Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
				<span className="ml-2 text-gray-600 dark:text-gray-400">Loading tags...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-8 text-center">
				<p className="text-red-500 dark:text-red-400">
					Failed to load tags. Please try again.
				</p>
			</div>
		);
	}

	return (
		<div className="p-6 mx-auto max-w-6xl">
			{/* Header */}
			<div className="flex justify-between items-center mb-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
						Tag Management
					</h1>
					<p className="mt-1 text-gray-600 dark:text-gray-400">
						Manage tags, merge duplicates, and organize your workflow tags.
					</p>
				</div>

				<div className="flex gap-2 items-center">
					<button
						onClick={recomputeUsage}
						disabled={recomputeUsageMutation.isPending}
						className="inline-flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
					>
						{recomputeUsageMutation.isPending ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							<RefreshCw className="w-4 h-4" />
						)}
						Recompute Usage
					</button>

					{selectedTags.size > 0 && (
						<button
							onClick={deleteSelectedTags}
							disabled={deleteTagMutation.isPending}
							className="inline-flex gap-2 items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
						>
							{deleteTagMutation.isPending ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<Trash2 className="w-4 h-4" />
							)}
							Delete Selected ({selectedTags.size})
						</button>
					)}
				</div>
			</div>

			{/* Search and Stats */}
			<div className="flex justify-between items-center mb-6">
				<div className="relative flex-1 max-w-md">
					<Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
					<input
						type="text"
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						placeholder="Search tags..."
						className="py-2 pr-4 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div className="text-sm text-gray-600 dark:text-gray-400">
					{filteredTags.length} of {tags.length} tags
				</div>
			</div>

			{/* Tags Grid */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
				{filteredTags.map(tag => {
					const isSelected = selectedTags.has(tag.id);
					const isEditing = editingTag?.id === tag.id;
					const colorStyles = getTagColor(tag.colorHue);

					return (
						<div
							key={tag.id}
							className={`p-4 border rounded-lg transition-colors ${
								isSelected
									? 'bg-blue-50 border-blue-500 dark:bg-blue-900/20'
									: 'bg-white border-gray-200 dark:border-gray-700 dark:bg-gray-800'
							}`}
						>
							<div className="flex justify-between items-start mb-3">
								<div className="flex gap-2 items-center">
									<input
										type="checkbox"
										checked={isSelected}
										onChange={() => toggleTagSelection(tag.id)}
										className="text-blue-600 rounded border-gray-300 focus:ring-blue-500"
									/>
									<div
										className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded-md border"
										style={colorStyles}
									>
										<Hash className="w-3 h-3" />
										{isEditing ? (
											<input
												type="text"
												value={editForm.name}
												onChange={e =>
													setEditForm(prev => ({
														...prev,
														name: e.target.value,
													}))
												}
												className="flex-1 min-w-0 bg-transparent border-none outline-none"
												autoFocus
											/>
										) : (
											<span className="font-medium">{tag.name}</span>
										)}
									</div>
								</div>

								<div className="flex gap-1 items-center">
									<span className="text-xs text-gray-500 dark:text-gray-400">
										{tag.usageCount} uses
									</span>
									<button
										onClick={() => startEditing(tag)}
										className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
									>
										<Edit3 className="w-3 h-3" />
									</button>
									<button
										onClick={() => startMerge(tag)}
										className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
									>
										<Merge className="w-3 h-3" />
									</button>
								</div>
							</div>

							{isEditing ? (
								<div className="space-y-2">
									<textarea
										value={editForm.description}
										onChange={e =>
											setEditForm(prev => ({
												...prev,
												description: e.target.value,
											}))
										}
										placeholder="Description (optional)"
										rows={2}
										className="px-2 py-1 w-full text-xs text-gray-900 bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
									/>
									<div className="flex gap-2">
										<button
											onClick={saveEdit}
											disabled={updateTagMutation.isPending}
											className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
										>
											{updateTagMutation.isPending ? (
												<Loader2 className="w-3 h-3 animate-spin" />
											) : (
												'Save'
											)}
										</button>
										<button
											onClick={cancelEdit}
											className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded dark:text-gray-300 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
										>
											Cancel
										</button>
									</div>
								</div>
							) : (
								<div>
									{tag.description && (
										<p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
											{tag.description}
										</p>
									)}
									<div className="text-xs text-gray-500 dark:text-gray-400">
										Created: {new Date(tag.createdAt).toLocaleDateString()}
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>

			{/* Empty State */}
			{filteredTags.length === 0 && (
				<div className="py-12 text-center">
					<Hash className="mx-auto mb-4 w-12 h-12 text-gray-300 dark:text-gray-600" />
					<p className="text-gray-500 dark:text-gray-400">
						{searchQuery ? 'No tags found matching your search.' : 'No tags found.'}
					</p>
				</div>
			)}

			{/* Merge Modal */}
			{showMergeModal && mergeFromTag && (
				<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70">
					<div className="p-6 mx-4 w-full max-w-md bg-white rounded-lg shadow-xl dark:bg-gray-800">
						<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
							Merge Tag
						</h3>

						<div className="space-y-4">
							<div>
								<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
									Merge "{mergeFromTag.name}" into:
								</label>
								<select
									value={mergeToTag?.id || ''}
									onChange={e => {
										const selectedTag = tags.find(t => t.id === e.target.value);
										setMergeToTag(selectedTag || null);
									}}
									className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
								>
									<option value="">Select target tag...</option>
									{tags
										.filter(t => t.id !== mergeFromTag.id)
										.map(tag => (
											<option key={tag.id} value={tag.id}>
												{tag.name} ({tag.usageCount} uses)
											</option>
										))}
								</select>
							</div>

							<p className="text-sm text-gray-600 dark:text-gray-400">
								All workflows tagged with "{mergeFromTag.name}" will be moved to the
								target tag. The source tag will be deleted.
							</p>
						</div>

						<div className="flex gap-3 mt-6">
							<button
								onClick={executeMerge}
								disabled={!mergeToTag || mergeTagsMutation.isPending}
								className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
							>
								{mergeTagsMutation.isPending ? (
									<Loader2 className="mx-auto w-4 h-4 animate-spin" />
								) : (
									'Merge Tags'
								)}
							</button>
							<button
								onClick={() => {
									setShowMergeModal(false);
									setMergeFromTag(null);
									setMergeToTag(null);
								}}
								className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md dark:text-gray-300 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
